import {select, takeEvery, take, fork, takeLatest} from "redux-saga/effects";

import {
    trackGtmStart,
    setupGenericVariables,
    trackVirtualPageView,
    trackCheckoutStep,
    trackCustomGAEvent,
    trackCheckoutCompleted,
    trackProductView,
    trackProductImpression,
    trackProductClick,
    trackAddToCart,
    trackRemoveFromCart,
    trackCheckoutOption,
    trackPurchase
} from "@montana/services/tracking";
import { isBrowser } from "@montana/helpers/browser.helper";
import {
    IGtmGenericVariables,
    IGtmProductWithQuantity,
    GtmCheckoutStep,
    GtmDevice,
    GtmEnvironment,
    IGtmProductFromList
} from "@montana/services/tracking/tracking";
import { loadScript } from "@montana/services/script-injection";

import { PageActionTypes, IFetchPageDataSuccessAction } from "../page/page.actions";
import { TrackingAction, ITrackPageViewAction, ITrackCheckoutStepAction, ITrackCustomEventAction, ITrackProductImpressionAction, ITrackProductClickAction, ITrackAddToCartAction, ITrackRemoveFromCartAction, ITrackPurchaseAction, ITrackCheckoutOptionAction } from "./tracking.actions";
import { IAuthenticationContainerState } from "../authentication/authentication";
import { IStoreState } from "@montana/store/store";
import { ILine, IPage } from "@montana/models/generated/cms.interface";
import { ICartContainerState } from "../cart";
import { isDefined } from "@montana/helpers/type.helpers";
import { staticSettings } from "@montana/helpers/static-settings.helper";
import { CartActionTypes } from "../cart/cart.actions";
import { PageKind } from "@montana/constants/page";
import { IPageContainerState } from "../page";
import { IProductContainerState } from "../product";
import { IDynamicSettingsContainerState } from "../dynamic-settings/dynamic-settings";

export const PAGE_TYPE_MAP: { [key: string]: string } = {
    [PageKind.CART]: "checkout",
    [PageKind.CHECKOUT]: "checkout",
    [PageKind.CONTENT]: "content",
    [PageKind.ERROR]: "error",
    [PageKind.FRONT]: "frontpage",
    [PageKind.LOGIN]: "login",
    [PageKind.NOTFOUND]: "notfound",
    [PageKind.PRODUCT_SERIES]: "product page",
    [PageKind.RECEIPT]: "checkout",
};

export const DEFAULT_PAGE_TYPE = "content";

enum DeviceType {
    PHONE = "(max-width: 500px)",
    TABLET = "(min-width: 501px) and (max-width: 1100px)"
}

let cartFirstTime = true;

function* onAppInit() {
    if (isBrowser) {
        yield setupTracking();
    }

}

function* onCartUpdatedFirstTime() {
    if (!cartFirstTime) {
        return;
    }
    const page: IPageContainerState = yield select((s: IStoreState) => s.page);
    const cart: ICartContainerState = yield select((s: IStoreState) => s.cart);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const pageTitle = (document.head.querySelector("title") || {} as any).innerHTML;

    if (page.kind === PageKind.NOTFOUND) {
        trackCustomGAEvent('404 page', 'page not found', document.location.pathname);
    }

    cartFirstTime = false;

    return yield fireVirtualPageView(page, cart.cartGuid, pageTitle);
}

function* onPageData(action: IFetchPageDataSuccessAction) {
    const cart: ICartContainerState = yield select((s: IStoreState) => s.cart);
    return yield fireVirtualPageView(action.payload, cart.cartGuid, action.payload.name);
}

function* onCheckoutStep({ step }: ITrackCheckoutStepAction) {
    return yield fireCheckoutStep(step);
}

function* onConfirmOrder() {
    return yield fireCheckoutStep(GtmCheckoutStep.THREE);
}

function onCustomEvent(action: ITrackCustomEventAction) {
    const { eventCategory, eventAction, eventLabel } = action;

    trackCustomGAEvent(eventCategory, eventAction, eventLabel);
}

// Private
function* setupTracking() {
    loadScript(
        `https://www.googletagmanager.com/gtm.js?id=${staticSettings.b2b ? 'GTM-KRP9XH9' : 'GTM-NSZ54JX'}`,
        {
            async: true,
        },
    );

    yield fireInitialTracking();
}

function* fireVirtualPageView(page: IPage, cartId: string, pageTitle?: string) {
    if (!pageTitle) {
        const { title }: ITrackPageViewAction = yield take(TrackingAction.PAGE_VIEW);
        pageTitle = title;
    }
    const pageType = PAGE_TYPE_MAP[page.kind!] || DEFAULT_PAGE_TYPE;

    trackVirtualPageView({
        pageUrl: location.href,
        pagePath: location.pathname,
        pageTitle,
        pageType,
        anonymousID: cartId
    });
}

function* fireCheckoutStep(step: number) {
    const { lines, totalPrice }: ICartContainerState = yield select((s: IStoreState) => s.cart);
    const products = lines?.map(l => lineToGtmProduct(l)).filter(isDefined) ?? [];

    trackCheckoutStep(step, products, totalPrice?.currency || '');
}

function* firePurchaseComplete() {
    const { lines, cartId, totalPrice, deliveryPrice }: ICartContainerState = yield select((s: IStoreState) => s.cart);
    const showPricePrUnit = true;
    const products = lines?.map(l => lineToGtmProduct(l, showPricePrUnit)).filter(isDefined) ?? [];

    trackCheckoutCompleted(`${cartId}`, totalPrice?.amount || 0, deliveryPrice?.amount || 0, products, totalPrice?.currency || '');
}

function lineToGtmProduct(line: ILine, showPricePrUnit?: boolean): IGtmProductWithQuantity | undefined {
    const { quantity, code, name, price, unitPrice, ean } = line;

    if (!quantity) {
        return;
    }

    return {
        name: name ?? '',
        id: code ?? '',
        quantity,
        variant: code ?? '',
        price: showPricePrUnit ? unitPrice?.amount ?? 0 : price?.amount ?? 0,
        ean
    };
}

function* fireInitialTracking() {
    const { user, markets }: IAuthenticationContainerState & IDynamicSettingsContainerState = yield select((s: IStoreState) => ({ ...s.authentication, ...s.dynamicSettings }));

    const isDev = staticSettings.isDebugging;

    const mobile = window.matchMedia(DeviceType.PHONE).matches ? GtmDevice.MOBILE : undefined;
    const tablet = window.matchMedia(DeviceType.TABLET).matches ? GtmDevice.TABLET : undefined;

    const genericVariables: IGtmGenericVariables = {
        deviceType: mobile || tablet || GtmDevice.DESKTOP,
        environment: !isDev ? GtmEnvironment.LIVE : GtmEnvironment.DEV,
        pageUrl: location.href,
        pagePath: location.pathname,
        ogLocation: location.href,
        bu: markets?.find(m => m.isSelected)?.marketId || 'en',
    };

    if (user && user.dealerId) {
        genericVariables.userID = user.dealerId;
    }

    setupGenericVariables(genericVariables);
    trackGtmStart();
}

function* onProductView() {
    const product: IProductContainerState = yield select((s: IStoreState) => s.product);
    trackProductView(product.prices?.priceWithDiscount?.currency || '', [{
        id: product.code || '',
        name: product.productTitleH1 || '',
        price: product.prices?.priceWithDiscount?.amount || 0,
        ean: product.prices?.ean
    }]);
}

function onProductImpression(action: ITrackProductImpressionAction) {
    const { products, category } = action;
    const currencyCode = !!products.length ? products[0].price?.priceWithDiscount?.currency || '' : '';
    const impressions: IGtmProductFromList[] = products.map((product, index) => ({
        id: product.code || '',
        name: product.displayName || '',
        price: product.price?.priceWithDiscount?.amount || 0,
        list: category || '',
        position: index + 1
    }));
    trackProductImpression(currencyCode, impressions);
}

function onProductClick(action: ITrackProductClickAction) {
    const { currency, product, category } = action;
    trackProductClick(currency, product, category);
}

function onAddToCart(action: ITrackAddToCartAction) {
    const { currency, products } = action;
    trackAddToCart(currency, products);
}

function onRemoveFromCart(action: ITrackRemoveFromCartAction) {
    const { currency, products } = action;
    trackRemoveFromCart(currency, products);
}

function onCheckoutOption(action: ITrackCheckoutOptionAction) {
    const { step, option } = action;
    trackCheckoutOption(step, option);
}

function onPurchase(action: ITrackPurchaseAction) {
    const { currency, products, actionField } = action;
    trackPurchase(currency, products, actionField);
}

export const trackingSagas = isBrowser
    ? [
        fork(onAppInit),
        takeLatest(CartActionTypes.UPDATE_CART, onCartUpdatedFirstTime),
        takeEvery(PageActionTypes.FETCH_DATA_SUCCESS, onPageData),
        takeEvery(TrackingAction.CHECKOUT_STEP, onCheckoutStep),
        takeEvery(CartActionTypes.PLACE_ORDER, onConfirmOrder),
        takeEvery(CartActionTypes.PLACE_ORDER, firePurchaseComplete),
        takeEvery(TrackingAction.CUSTOM_EVENT, onCustomEvent),
        takeEvery(TrackingAction.PRODUCT_VIEW, onProductView),
        takeEvery(TrackingAction.PRODUCT_IMPRESSION, onProductImpression),
        takeEvery(TrackingAction.PRODUCT_CLICK, onProductClick),
        takeEvery(TrackingAction.ADD_TO_CART, onAddToCart),
        takeEvery(TrackingAction.REMOVE_FROM_CART, onRemoveFromCart),
        takeEvery(TrackingAction.CHECKOUT_OPTION, onCheckoutOption),
        takeEvery(TrackingAction.PURCHASE, onPurchase),
    ]
    : [];
